import qs from 'querystring';
import './styles.modules.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './styles.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { Col, Modal, ModalHeader, Row } from 'reactstrap';
import LoadingContainer from '../LoadingContainer';
import ProductCartForm from '../ProductCartForm';
import { Product } from '~domain/product/Product';
import { clearProductCart } from '~redux/cart/actions';
import { cartProductSelector } from '~redux/cart/selectors';
import { getProductCart } from '~redux/cart/thunk';
import { formatCurrency } from '~utils/formatter';

type TypeSelectModalProps = {
  productId: string;
  isOpen: boolean;
  toggle: () => void;
  cart?: boolean;
  index: number;
};

const TypeSelectModal: React.FC<TypeSelectModalProps> = ({
  productId,
  isOpen,
  toggle,
  cart,
  index,
}) => {
  //constants
  const dispatch = useDispatch();
  const cartProduct = useSelector(cartProductSelector);
  const product = useMemo(
    () => (cartProduct ? Product.create(cartProduct) : null),
    [cartProduct]
  );
  const productOrderLot = useMemo(() => product?.product_order_lot ?? 1, [
    product,
  ]);
  const minimum = useMemo(
    () => product?.product_minimum_quantity ?? productOrderLot,
    [product, productOrderLot]
  );
  const history = useHistory();
  const is_direct = !!qs.parse(history.location.search?.split('?')?.[1])
    ?.direct;
  const [isCompleted, setIsCompleted] = useState(false);
  const [image, setImage] = useState<string>();

  //callbacks
  const selectColor = useCallback(
    (color) => {
      const productType = product!.producttype.find(
        (type) => type.color === color
      );
      if (!productType?.imageKey) {
        return;
      }
      setImage(productType.imageUrl!);
    },
    [product]
  );

  const selectSize = useCallback(
    (size) => {
      const productType = product!.producttype.find(
        (type) => type.size === size
      );
      if (!productType?.imageKey) {
        return;
      }
      setImage(productType.imageUrl!);
    },
    [product]
  );

  const selectType = useCallback(
    (type) => {
      const productType = product!.producttype.find(
        (t) => t.type_name === type
      );
      if (!productType?.imageKey) {
        return;
      }
      setImage(productType.imageUrl!);
    },
    [product]
  );

  //effects
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    dispatch(getProductCart(productId));
    return () => {
      dispatch(clearProductCart());
    };
  }, [productId, isOpen]);

  useEffect(() => {
    if (!product?.producttype?.length) {
      return;
    }
    if (product.producttype[0].color) {
      selectColor(product.producttype[0].color);
    }
    if (product.producttype[0].type_name) {
      selectType(product.producttype[0].type_name);
    }
  }, [product?.producttype?.length]);

  useEffect(() => {
    if (!product?.imageKeys?.length) {
      return;
    }
    setImage(product.imageKeys[0].imageUrl);
  }, [product?.imageKeys?.length]);

  useEffect(() => {
    if (!isCompleted) {
      return;
    }
    setTimeout(() => {
      setIsCompleted(false);
      toggle();
    }, 300);
  }, [isCompleted]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle} />
      {!product && <LoadingContainer isLoading />}
      {product && (
        <>
          <Row className="mb-4">
            <Col xs="4">
              <img
                className="type-select-modal-img"
                src={image}
                alt="type image"
              />
            </Col>
            <Col>
              <h5>{product.product_name}</h5>
              <p>
                <b>{formatCurrency(product.unitPrice)}</b>
                （上代：
                {product.is_open_price
                  ? 'オープン価格'
                  : formatCurrency(product.product_retail_price)}
                ）
              </p>
              <p>最低発注数量：{minimum}</p>
              <p>
                <Link
                  className="type-select-modal-link"
                  to={`/productdetail/${product.id}`}
                >
                  商品詳細
                </Link>
              </p>
            </Col>
          </Row>
          <ProductCartForm
            product={product as Product}
            productTypes={product.producttype}
            onSelectColor={selectColor}
            onSelectSize={selectSize}
            onSelectType={selectType}
            isDirect={is_direct}
            cart={cart}
            onSubmit={() => setIsCompleted(true)}
          />
        </>
      )}
    </Modal>
  );
};

export default TypeSelectModal;
